import { httpClient } from '@/lib/api/client';

export interface LaunchBatchJobRequest {
  samplesheet_s3_path: string;
  job_name: string;
}

export interface BatchJob {
  RunId: string;
  RunName: string;
  Status: string;
  CreatedAt: string;
  LaboratoryId: string;
  OrganizationId: string;
  UserId: string;
  Owner: string;
  WorkflowName: string;
  SampleSheetS3Url?: string;
  OutputS3Url?: string;
  Settings?: string;
  StoppedAt?: string;
  LogStreamName?: string;
  Logs?: string[];
}

export type LaunchBatchJobResponse = BatchJob;

export interface ListJobsResponse {
  message: string;
  jobs: BatchJob[];
}

export interface BatchPipeline {
  pipelineId: string;
  name: string;
  description: string;
}

export interface PipelineSchema {
  properties: Record<
    string,
    {
      type: string;
      description: string;
      required: boolean;
    }
  >;
}

class BatchJobsModule {
  async launchJob(apiBaseUrl: string, jobRequest: LaunchBatchJobRequest): Promise<LaunchBatchJobResponse> {
    const res = await httpClient.callAwsBatch<LaunchBatchJobResponse>(
      apiBaseUrl,
      'POST',
      '/aws-batch/launch-pipeline',
      jobRequest,
    );

    if (!res) {
      throw new Error('Failed to launch batch job');
    }

    return res;
  }

  async listJobs(apiBaseUrl: string, laboratoryId: string): Promise<BatchJob[]> {
    const res = await httpClient.callAwsBatch<ListJobsResponse>(
      apiBaseUrl,
      'GET',
      `/aws-batch/list-jobs?laboratoryId=${laboratoryId}`,
    );

    if (!res) {
      throw new Error('Failed to retrieve batch jobs');
    }

    return res.jobs || [];
  }

  async getJobStatus(apiBaseUrl: string, jobId: string, laboratoryId?: string): Promise<BatchJob> {
    const query = laboratoryId ? `?laboratoryId=${laboratoryId}` : '';
    const res = await httpClient.callAwsBatch<BatchJob>(
      apiBaseUrl,
      'GET',
      `/aws-batch/read-job-status/${jobId}${query}`,
    );

    if (!res) {
      throw new Error('Failed to retrieve job status');
    }

    return res;
  }

  async listPipelines(): Promise<BatchPipeline[]> {
    return [
      {
        pipelineId: 'aws-batch-pipeline-1',
        name: 'AWS Batch Genomics Pipeline',
        description: 'Standard genomics analysis pipeline running on AWS Batch',
      },
    ];
  }

  async getPipeline(pipelineId: string): Promise<BatchPipeline> {
    const pipelines = await this.listPipelines();
    const pipeline = pipelines.find((p) => p.pipelineId === pipelineId);
    if (!pipeline) {
      throw new Error(`Pipeline ${pipelineId} not found`);
    }
    return pipeline;
  }

  async getPipelineSchema(): Promise<PipelineSchema> {
    return {
      properties: {
        samplesheet_s3_path: {
          type: 'string',
          description: 'S3 path to samplesheet CSV file',
          required: true,
        },
        job_name: {
          type: 'string',
          description: 'Name for this batch job',
          required: true,
        },
      },
    };
  }
}

export default BatchJobsModule;
